// frontend/shared-ui/src/components/Layout.jsx
import React, { useEffect, useState, useCallback } from "react";

const MOBILE_BREAKPOINT = 768;

export default function Layout({ sidebar, topbar, children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT,
  );

  // Track viewport size reactively
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      // Auto-close drawer when leaving mobile
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    if (isMobile && mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isMobile, mobileOpen]);

  // Close drawer on Escape
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => e.key === "Escape" && setMobileOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const toggleSidebar = useCallback(() => {
    if (isMobile) setMobileOpen((v) => !v);
    else setCollapsed((v) => !v);
  }, [isMobile]);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  return (
    <div className={`fh-layout ${mobileOpen ? "fh-layout--drawer-open" : ""}`}>
      {typeof sidebar === "function"
        ? sidebar({
            collapsed,
            mobileOpen,
            isMobile,
            closeMobile,
            toggleSidebar,
          })
        : sidebar}

      {/* Mobile backdrop — tap to close */}
      {isMobile && mobileOpen && (
        <div
          className="fh-layout__backdrop"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      <div className="fh-layout__main">
        {typeof topbar === "function"
          ? topbar({ onToggleSidebar: toggleSidebar })
          : topbar}

        <main className="fh-layout__content fh-scroll">{children}</main>
      </div>
    </div>
  );
}
